import uniq from 'lodash/uniq'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { type Return } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import {
  useRefreshShiftsRows,
  useRefreshStats,
} from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { ShiftParam } from 'modules/shift_management/lib/buildAddShiftsParameters'
import buildEmployeeIds from 'modules/shift_management/lib/buildEmployeeIds'
import { trackEditShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import shiftsCollection from 'modules/shift_management/store/api/collections/shifts'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import { useBulkCreatePlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useBulkCreatePlannedBreaks'
import { useBulkCudPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useBulkCudPlannedBreaks'

import buildAddPlannedBreaksParameters from '../lib/buildAddPlannedBreaksParameters'
import type PlannedBreakForm from '../lib/forms/PlannedBreakForm'
import { BulkPatchProps } from './useEditShifts'
import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

interface ReassignShiftProps {
  shifts: Shift[]
  editShiftsParams: BulkPatchProps
  assignShiftsParams: ShiftParam[]
  isOriginalIncluded: boolean
  plannedBreakForms: PlannedBreakForm[]
  through: 'modal' | 'side_panel'
  contractVersions: ContractVersions
}

export interface ReturnProps {
  onReassignShifts: (closePortal?: ClosePortalFn) => Promise<Return<void>>
  submittingOnReassign: boolean
}

const useReassignShifts = ({
  shifts,
  editShiftsParams,
  assignShiftsParams,
  isOriginalIncluded,
  plannedBreakForms,
  through,
  contractVersions,
}: ReassignShiftProps): ReturnProps => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { refreshShiftsRows } = useRefreshShiftsRows(contractVersions)
  const { bulkCud } = useBulkCudPlannedBreaks()
  const { bulkCreate } = useBulkCreatePlannedBreaks()
  const { refreshStats } = useRefreshStats(view.startAt, view.endAt)

  const { running: submittingOnReassign, performAction } = useAsyncAction(
    async (closePortal?: ClosePortalFn) => {
      const assignedToModified = true
      const shiftsIds = shifts.map((shift) => shift.id)
      // Get all shift rows that have shifts by employees affected by the edit action to update stats
      const shiftsEmployeeIds = uniq(shifts.map((shift: Shift) => shift.employeeId))
      const relatedShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
        const employeeIds = new Set(row.employeeIds)
        return shiftsEmployeeIds.some((id: number) => employeeIds.has(id))
      })

      // If employee is included edit the corresponding shifts
      if (isOriginalIncluded) {
        await shiftsCollection.rpc('bulk_patch', {
          shift_ids: shiftsIds,
          start_at: editShiftsParams.startAt,
          end_at: editShiftsParams.endAt,
          notes: editShiftsParams.notes,
          extra_hours: editShiftsParams.extraHours,
          location_id: editShiftsParams.locationId,
          work_area_id: editShiftsParams.workAreaId,
        })

        // Edit existing break of original employee (same logic as in edit shifts)
        if (plannedBreakForms && shifts.length === 1) {
          const shiftId = shifts[0]?.id
          const plannedBreaks = buildAddPlannedBreaksParameters(plannedBreakForms, shifts)
          await asyncAction(() => bulkCud({ plannedBreaksParams: [{ shiftId, plannedBreaks }] }), {
            successFlash: false,
          })
        }
      } else {
        // If employee is not included destroy corresponding shifts
        await shiftsCollection.rpc('bulk_destroy', { ids: shiftsIds })
      }

      await refreshShiftsRows(relatedShiftsRows, [])

      // And then create shifts to newly selected employees
      const { success, value } = await asyncAction(
        async () => await shiftsCollection.rpc('bulk_create', { shifts: assignShiftsParams }),
        {
          successFlash: false,
        }
      )

      if (success) {
        const newShifts = value.map((shift: ShiftPayload) => Shift.buildFromRequest(shift))
        // Create planned breaks for new shift (same logic as in edit shifts)
        // If shift is default we want to bypass this because breaks are already
        // created when creating shifts so if we don't bypass this we create
        // duplicated breaks
        if (plannedBreakForms && newShifts.length === 1 && !newShifts[0]?.isDefault) {
          const plannedBreaks = buildAddPlannedBreaksParameters(plannedBreakForms, newShifts)
          await asyncAction(() => bulkCreate({ plannedBreaks }), { successFlash: false })
        }

        const { employeeIds, newShiftsRowsKeys, shiftsReferenceKeys } = buildEmployeeIds(
          view,
          shiftsRows,
          newShifts
        )

        if (newShiftsRowsKeys.length !== 0) {
          await refreshShiftsRows([], employeeIds)
        }

        if (newShiftsRowsKeys.length === 0) {
          // Get shiftsRows where there are affected shifts to update stats
          // This is not necessary when we have added new shiftsRows, as
          // refreshShiftsRows has already fetched the stats
          const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
            return shiftsReferenceKeys.includes(row.key)
          })

          await refreshStats(previousShiftsRows)
        }

        dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: newShifts, view })
        dispatchSchedule({ type: 'ADD_SHIFTS', shifts: newShifts })
      }

      const totalNumberOfAssignedEmployees = isOriginalIncluded
        ? shiftsEmployeeIds.length + assignShiftsParams.length
        : assignShiftsParams.length

      await trackEditShiftSubmit({
        shifts: shifts,
        name: editShiftsParams.name,
        startAt: editShiftsParams.startAt,
        endAt: editShiftsParams.endAt,
        notes: editShiftsParams.notes,
        extraHours: editShiftsParams.extraHours,
        locationId: editShiftsParams.locationId,
        workAreaId: editShiftsParams.workAreaId,
        mixedValueNotes: editShiftsParams.mixedValueNotes,
        view,
        assignedToModified,
        originalEmployeeIncluded: isOriginalIncluded,
        totalNumberOfAssignedEmployees,
        through,
      })

      if (closePortal) closePortal()

      // Clear selection at the end to not remove the edit shifts button,
      // because the sidepanel and modal are rendered from it.
      dispatchSelection({
        type: 'CLEAR_SELECTION',
      })
    },
    {
      successMessage: i18n.t('shift_management.actions.edit_shifts_success'),
    }
  )

  return {
    onReassignShifts: performAction,
    submittingOnReassign,
  }
}

export default useReassignShifts
